import React, { useImperativeHandle, useRef, useCallback } from 'react';

import { GroupContextProvider, useGroupContext } from './context';
import type { GroupImperativeHandle, GroupProps, Layout } from './types';
import { useId } from './use-id';

// Inner component that has access to context
const GroupInner: React.FC<{
  groupId: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  elementRef?: React.Ref<HTMLDivElement>;
  rest: Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'className' | 'style' | 'id' | 'data-group' | 'data-testid'
  >;
}> = ({ groupId, className, style, children, elementRef, rest }) => {
  const { groupElementRef } = useGroupContext();

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      groupElementRef.current = node;
      if (elementRef) {
        if (typeof elementRef === 'function') {
          elementRef(node);
        } else if (elementRef && typeof elementRef === 'object' && 'current' in elementRef) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (elementRef as any).current = node;
        }
      }
    },
    [elementRef, groupElementRef],
  );

  return (
    <div
      ref={setRefs}
      id={String(groupId)}
      data-group
      data-testid={String(groupId)}
      className={className}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
};

export const Group = React.forwardRef<GroupImperativeHandle, GroupProps>(
  (
    {
      children,
      className,
      defaultLayout,
      disableCursor = false,
      disabled = false,
      elementRef,
      groupRef,
      id: idProp,
      onLayoutChange,
      orientation = 'horizontal',
      style,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const groupId = idProp ?? generatedId;
    const layoutRef = useRef<Layout>(defaultLayout ?? {});

    // Expose imperative handle
    useImperativeHandle(
      ref || groupRef,
      (): GroupImperativeHandle => ({
        getLayout: () => {
          return { ...layoutRef.current };
        },
        setLayout: (layout: Layout) => {
          layoutRef.current = layout;
          // TODO: Apply layout to panels
          return layout;
        },
      }),
      [],
    );

    const handleLayoutChange = useCallback(
      (layout: Layout) => {
        layoutRef.current = layout;
        onLayoutChange?.(layout);
      },
      [onLayoutChange],
    );

    return (
      <GroupContextProvider
        groupId={String(groupId)}
        orientation={orientation}
        disabled={disabled}
        disableCursor={disableCursor}
        onLayoutChange={handleLayoutChange}
      >
        <GroupInner
          groupId={String(groupId)}
          className={className}
          style={{
            display: 'flex',
            flexDirection: orientation === 'horizontal' ? 'row' : 'column',
            height: '100%',
            width: '100%',
            ...style,
          }}
          elementRef={elementRef}
          rest={rest}
        >
          {children}
        </GroupInner>
      </GroupContextProvider>
    );
  },
);

Group.displayName = 'Group';
